import {useEffect, useState, useRef} from 'react';
// TODO: this is a nightnmare - we need to have a user preference context / gobal state management
// just added even more complexity with the localStorage fallback for unauthenticated users (public or token)

// NOTE: ultimately we should have a user preference context but atm it is not worth doing since we
// are so far from having our entire app wrapped/converted to react
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import {setPreference} from '@netx/core-user-next/utils/setPreference';
import {isPublicUser} from '@netx/core-user-next/utils/isPublicUser';
import {session} from '@netx/core-auth/collections/singleton/session';
import useEvent, {useConstCallback} from './useEvent';
import {userPrefs} from '@netx/core-user/collections/singletons/userPreferences';

type PrefValue<Bool extends boolean = false> = Bool extends true
	? boolean
	: string | undefined;
type PrefInValue<Bool extends boolean = false> = Bool extends true
	? boolean
	: any | undefined;
type PrefOutValue<Bool extends boolean = false> = Bool extends true
	? boolean
	: boolean | string | number | undefined;
type ParseInFn<Bool extends boolean = false, I = PrefInValue<Bool>> = (
	value: PrefValue<Bool>,
) => I;
type ParseOutFn<
	Bool extends boolean = false,
	I = PrefInValue<Bool>,
	O = PrefOutValue<Bool>,
> = (value: I) => O;

type DefaultValue<
	Bool extends boolean = false,
	I = PrefInValue<Bool>,
> = I extends undefined ? PrefValue<Bool> : I;

type Options<
	Bool extends boolean = false,
	I extends PrefInValue<Bool> | undefined = undefined,
	O extends PrefOutValue<Bool> | undefined = PrefOutValue<Bool>,
	// D extends DefaultValue<Bool, I> | undefined = undefined,
> = {
	asBoolean?: Bool;
	defaultValue?: DefaultValue<Bool, I>;
	onChange?: (value: PrefValue<Bool>) => void;
	parseOut?: ParseOutFn<Bool, I extends undefined ? PrefValue<Bool> : I, O>;
} & (I extends PrefInValue<Bool>
	? {parseIn?: ParseInFn<Bool, I>}
	: {parseIn?: (value: PrefValue<Bool>) => PrefValue<Bool>});

// type UseUserPrefProps<
// 	Bool extends boolean = false,
// 	I extends PrefInValue<Bool> | undefined = undefined,
// 	O extends PrefOutValue<Bool> | undefined = PrefOutValue<Bool>,
// 	D extends DefaultValue<Bool, I> | undefined = undefined,
// > = {

// } & (I extends PrefInValue<Bool> ? {options: Options<Bool, I, O, D>} : {options?: Options<Bool, I, O, D>})

const getLocalStorageValue = (
	preference: string,
	asBoolean: boolean,
	defaultValue?: any,
) => {
	try {
		const value = window.localStorage.getItem(preference);
		if (value == null) return defaultValue ?? (asBoolean ? false : undefined);
		return asBoolean ? value.toLowerCase() === 'true' : value;
	} catch (e) {
		return defaultValue ?? (asBoolean ? false : undefined);
	}
};

const setLocalStorageValue = (
	preference: string,
	value: any,
	asBoolean: boolean,
) => {
	try {
		window.localStorage.setItem(
			preference,
			value != null ? value.toString() : asBoolean ? 'false' : '',
		);
	} catch (e) {}
};

/**
 * Get and track value for a user preference
 * @param preference User preference
 * @param asBoolean Value as boolean
 * @example
   const metadataLookupsEnabled = useUserPref('category.metadataDatasetLookupEnabled', true);
 */
export function useUserPref<
	Bool extends boolean = false,
	I extends PrefInValue<Bool> | undefined = undefined,
	O extends PrefOutValue<Bool> | undefined = PrefOutValue<Bool>,
>(preference = '', options?: Options<Bool, I, O>) {
	const {asBoolean, parseIn, parseOut, defaultValue = null} = options || {};

	const parseInRef = useRef(parseIn);
	parseInRef.current = parseIn;

	const _getPreference = useConstCallback(() => {
		return session.getToken() || isPublicUser()
			? getLocalStorageValue(preference, asBoolean === true, defaultValue)
			: getPreference(preference, asBoolean);
	});

	const _setUserPreference = useConstCallback((value: O | PrefValue<Bool>) => {
		if (preference === '') return;
		return session.getToken() || isPublicUser()
			? setLocalStorageValue(preference, value, asBoolean === true)
			: setPreference(preference, value);
	});

	const [value, setValue] = useState<I extends undefined ? PrefValue<Bool> : I>(
		parseIn ? parseIn(_getPreference() as PrefValue<Bool>) : _getPreference(),
	);

	useEffect(() => {
		if (preference === '') return;

		const update = () => {
			const prefValue = _getPreference();
			setValue(
				parseInRef.current
					? parseInRef.current(prefValue)
					: prefValue == null
					? defaultValue
					: prefValue,
			);
		};
		userPrefs.on('change sync saved', update);
		window.addEventListener('storage', update);

		update();

		return () => {
			userPrefs.off('change sync saved', update);
			window.removeEventListener('storage', update);
		};
	}, [preference, asBoolean, defaultValue]);

	const setPref = useEvent(
		(newValue: typeof value | ((newValue: typeof value) => typeof value)) => {
			const _value =
				typeof newValue === 'function'
					? (newValue as (newValue: typeof value) => typeof value)(value)
					: newValue;
			const prefValue = parseOut ? parseOut(_value) : _value;
			setValue(_value);
			_setUserPreference(prefValue);
		},
	);

	const unsetPref = useEvent(() => {
		setValue(parseIn ? parseIn(undefined as any) : undefined);
		_setUserPreference('' as any);
	});

	return [value, setPref, unsetPref] as const;
}
export default useUserPref;
